import { HttpErrorResponse } from '@angular/common/http';
import { Component, HostListener, inject, signal } from '@angular/core';
import { email, form, FormField, FormRoot, required, submit } from '@angular/forms/signals';
import { RouterModule } from '@angular/router';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { LangToggleComponent } from '@app/shared/components/lang-toggle/lang-toggle.component';
import { AuthService } from '@app/shared/services/auth.service';
import { LoginRequestBody } from '@app/shared/types/models/auth/auth.model';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'proffeo-in',
  templateUrl: './sign-in.component.html',
  imports: [TranslatePipe, FormField, FormRoot, RouterModule, LangToggleComponent, UiButtonComponent, UiInputComponent]
})
export class SignInComponent {
  private readonly authService: AuthService = inject(AuthService);
  private readonly translateService: TranslateService = inject(TranslateService);

  protected readonly model = signal<LoginRequestBody>({ username: '', password: '' });
  protected readonly form = form(this.model, path => {
    required(path.username, { message: 'sign-in-email-address-is-required' });
    email(path.username, { message: 'sign-in-please-enter-a-valid-email-address' });
    required(path.password, { message: 'sign-in-password-is-required' });
  });
  protected readonly signInError = signal('');

  @HostListener('window:keydown.enter')
  private onEnterKey(): void {
    this.signIn();
  }

  protected signIn(): void {
    void submit(this.form, {
      action: async () => {
        try {
          // A sign-in superseded by a newer session completes without a value - nothing to report then.
          await firstValueFrom(this.authService.signIn(this.model()), { defaultValue: undefined });
        } catch (error) {
          this.signInError.set(this.toErrorMessage(error as HttpErrorResponse));
        }
      }
    });
  }

  private toErrorMessage(error: HttpErrorResponse): string {
    if (error.status === 401) {
      return this.translateService.instant('sign-in-invalid-credentials');
    }
    if (error.status === 500) {
      return this.translateService.instant('sign-in-internal-server-error');
    }
    return error.error?.Detail ?? '';
  }
}
